// Hook encapsulating all state and handlers for the ProfileModal component.
'use client';

import { useState, useEffect, useCallback } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { applyServerErrors, useRevalidateAfterSubmit } from '@auctogon/ui/web';
import { formatPhoneE164 } from '@auctogon/core/validation/phone';
import {
  StaffProfileFormSchema,
  type StaffProfileFormValues,
} from '@auctogon/core-admin/schemas/staff-profile';
import { useAuth } from '../../auth/AuthProvider';
import { useAuthOps } from '../../session/SessionProvider';
import { useTenant } from '../../tenant/TenantProvider';
import { useStaffNotificationPreferences } from './useStaffNotificationPreferences';
import type {
  ProfileTab,
  UserProfile,
  PasswordFields,
  PasswordVisibility,
} from './profile-modal-helpers';
import {
  fetchUserProfile,
  saveProfileToServer,
  changePasswordOnServer,
} from './profile-modal-helpers';

/** Arguments passed to the hook from the parent modal. */
export interface UseProfileModalArgs {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Map a loaded profile onto the form's values. PhoneInput speaks E.164, and so
 * does the schema's phone rule.
 * @param loaded - The profile fetched on open
 * @returns The form's reset values
 */
function toFormValues(loaded: UserProfile): StaffProfileFormValues {
  return {
    firstName: loaded.firstName,
    lastName: loaded.lastName,
    phone: loaded.phone ? formatPhoneE164(loaded.phone) : '',
  };
}

/**
 * The Profile tab's RHF form over the shared schema (BUG-2567, rule #27), so a
 * server `fieldErrors` entry lands under its input.
 * @returns The configured form instance
 */
function useProfileForm() {
  const form = useForm<StaffProfileFormValues>({
    resolver: zodResolver(StaffProfileFormSchema),
    defaultValues: { firstName: '', lastName: '', phone: '' },
    mode: 'onSubmit',
  });
  useRevalidateAfterSubmit(form);
  return form;
}

/**
 * PATCH the validated profile values and route a failure onto the form.
 * BUG-2567: server `fieldErrors` land under their inputs, anything else (and a
 * network failure) in the form's root error, which the modal shows as the banner.
 * @param opts.form - The Profile tab's RHF instance
 * @param opts.values - The schema-validated form values
 * @param opts.refreshUser - Re-reads the session after a confirmed save
 * @returns The saved response, or `null` when an error is now showing on the form
 * @sideEffect One PATCH /api/user/profile; refreshes the session; calls `form.setError` on failure.
 */
async function submitProfileValues(opts: {
  form: UseFormReturn<StaffProfileFormValues>;
  values: StaffProfileFormValues;
  refreshUser: () => Promise<unknown>;
}) {
  const { form, values, refreshUser } = opts;
  try {
    const saved = await saveProfileToServer({
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone ?? '',
    });
    if (!saved.success) {
      applyServerErrors(form, saved);
      return null;
    }
    await refreshUser();
    return saved;
  } catch (err) {
    form.setError('root', {
      type: 'server',
      message: err instanceof Error ? err.message : 'Failed to save profile',
    });
    return null;
  }
}

/**
 * Manages all ProfileModal state: profile form, passwords, notifications,
 * loading/saving indicators, and success/error messages.
 * @param args - Modal open state and close callback
 * @returns All state values and handler functions for the modal
 */
export function useProfileModal({ isOpen, onClose }: UseProfileModalArgs) {
  const { user, refreshUser } = useAuth();
  const authOps = useAuthOps();
  const { currentTenant } = useTenant();

  // `profile` keeps only what the form never edits (display name, email).
  const form = useProfileForm();
  const { reset: resetForm } = form;

  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [profile, setProfile] = useState<UserProfile>({
    firstName: '', lastName: '', name: '', email: '', phone: '',
  });
  // BL-1230: the notifications tab owns its own state — see the hook's header
  // for why it isn't folded in here.
  const notificationPrefs = useStaffNotificationPreferences({
    isOpen,
    tenantId: currentTenant?.id,
  });
  const [passwords, setPasswords] = useState<PasswordFields>({
    current: '', new: '', confirm: '',
  });
  const [showPasswords, setShowPasswords] = useState<PasswordVisibility>({
    current: false, new: false, confirm: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => { setMounted(true); }, []);

  // Load fresh user data from the API when the modal opens
  useEffect(() => {
    if (!isOpen || !user) return;
    setIsLoading(true);
    fetchUserProfile(user)
      .then((loaded) => {
        setProfile(loaded);
        resetForm(toFormValues(loaded));
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, user, resetForm]);

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  /** Save profile name and phone to the server. */
  const handleSaveProfile = useCallback(async () => {
    if (!user) return;
    setSuccess(null);
    // A stale Security-tab password error must not shadow this save's result.
    setError(null);
    await form.handleSubmit(async (values) => {
      setIsSaving(true);
      try {
        const saved = await submitProfileValues({ form, values, refreshUser });
        if (!saved) return;
        // BUG-1650: the header updates from the server-composed display name.
        setProfile((prev) => ({ ...prev, name: saved.profile?.name ?? prev.name }));
        window.dispatchEvent(new CustomEvent('user-updated'));
        setSuccess('Profile updated successfully');
        setTimeout(() => onClose(), 1000);
      } finally {
        setIsSaving(false);
      }
    })();
  }, [user, form, refreshUser, onClose]);

  /** Validate and submit a password change. */
  const handleChangePassword = useCallback(async () => {
    if (passwords.new !== passwords.confirm) {
      setError('New passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await changePasswordOnServer({
        currentPassword: passwords.current,
        newPassword: passwords.new,
        changePassword: authOps.changePassword,
      });
      setPasswords({ current: '', new: '', confirm: '' });
      setSuccess('Password updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsSaving(false);
    }
  }, [passwords, authOps]);

  return {
    mounted, activeTab, setActiveTab,
    profile, form,
    ...notificationPrefs,
    passwords, setPasswords,
    showPasswords, setShowPasswords,
    isSaving, isLoading, error, success,
    handleSaveProfile, handleChangePassword,
  };
}
